import { DND_DROP_EDGE_ATTR, DND_DROP_TARGET_ATTR, DND_DROP_VALID_ATTR } from './attributes'
import { emitHoverChange, emitTargetChange } from './registry'
import type { Gesture } from './scope'
import type { DndEdge } from './types'

// The hovered droppable, shared by the pointer and keyboard paths. One place
// marks it and reports it, so both paths emit hover and target-change in the
// same order. Safe as a singleton because only one gesture runs at a time.

export type DropTarget = {
    element: HTMLElement
    dropId: string | undefined
    valid: boolean
    /** Pointer path only; keyboard gestures keep this null. */
    edge: DndEdge | null
}

let current: DropTarget | null = null

export function currentDropTarget(): DropTarget | null {
    return current
}

export function clearTargetAttributes(element: HTMLElement): void {
    element.removeAttribute(DND_DROP_TARGET_ATTR)
    element.removeAttribute(DND_DROP_VALID_ATTR)
    element.removeAttribute(DND_DROP_EDGE_ATTR)
}

function stampEdge(element: HTMLElement, edge: DndEdge | null): void {
    if (edge === null) element.removeAttribute(DND_DROP_EDGE_ATTR)
    else element.setAttribute(DND_DROP_EDGE_ATTR, edge)
}

function setCurrent(gesture: Gesture, target: DropTarget): void {
    current = target
    emitHoverChange(target.element, { payload: gesture.payload, canDrop: target.valid, edge: target.edge })
    emitTargetChange(gesture, { dropId: target.dropId, dropElement: target.element, valid: target.valid, edge: target.edge })
}

export function enterDropTarget(gesture: Gesture, target: DropTarget): void {
    target.element.setAttribute(DND_DROP_TARGET_ATTR, 'true')
    target.element.setAttribute(DND_DROP_VALID_ATTR, target.valid ? 'true' : 'false')
    stampEdge(target.element, target.edge)
    setCurrent(gesture, target)
}

/** Same target, new pointer half: re-emits so consumers can move the gap indicator. */
export function changeDropTargetEdge(gesture: Gesture, edge: DndEdge | null): void {
    if (current === null) return
    stampEdge(current.element, edge)
    setCurrent(gesture, { ...current, edge })
}

// The leave reports through the gesture that is still set.
export function clearDropTarget(gesture: Gesture, emitLeave = true): void {
    if (current === null) return
    const left = current.element
    clearTargetAttributes(left)
    current = null
    // emitLeave only covers the zone and kit, which hear the next enter and can
    // infer the leave from it. The droppable never hears another droppable's
    // enter, so it always gets its own leave.
    emitHoverChange(left, null)
    if (emitLeave) emitTargetChange(gesture, null)
}
